/**
 * CustomReportPage — MVP de Reportes Personalizados
 *
 * Wizard de 4 pasos:
 *   1. Entidad (ventas / compras)
 *   2. Campos (checklist agrupado)
 *   3. Filtros (date range, status multi-select, search, amount range)
 *   4. Formato + ejecutar (xlsx / csv)
 *
 * Sync: ≤5000 filas → descarga directa del blob.
 * Async: >5000 → 202, se muestra aviso "te enviaremos por email".
 */
import { todayISO } from '../../../lib/utils'
import { useMemo, useState } from 'react'
import {
  FileSpreadsheet, FileText, Building2, Users, ArrowLeft, ArrowRight,
  Check, Loader2, Sparkles, Download, Mail, AlertTriangle, CheckCircle2,
} from 'lucide-react'
import DatePicker from '@/components/DatePicker'
import { Button } from '@/components/Button'
import { labelCls } from '@/lib/formStyles'
import {
  useCustomReportSchema, useGenerateCustomReport, triggerBlobDownload,
  type ReportEntity, type ReportFormat, type CustomFieldSpec, type CustomReportFilters,
} from '../hooks/useCustomReports'
import { useSellers } from '@/modules/users/hooks/useUsers'
import { SelectField } from '@/components/ds/SelectField'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function todayIso(): string {
  return todayISO()
}

function firstOfMonthIso(): string {
  return todayIso().substring(0, 7) + '-01'
}

const TYPE_LABELS: Record<string, string> = {
  string: 'texto',
  number: 'número',
  date: 'fecha',
  datetime: 'fecha/hora',
  boolean: 'sí/no',
}

// ─── Entity picker data ──────────────────────────────────────────────────────

const ENTITIES: { key: ReportEntity; label: string; description: string; icon: typeof Users }[] = [
  {
    key: 'sales',
    label: 'Documentos de Venta',
    description: 'Facturas, boletas, notas de crédito y débito emitidas.',
    icon: Users,
  },
  {
    key: 'purchases',
    label: 'Facturas de Proveedor',
    description: 'Facturas recibidas, incluyendo RCV y DTE de proveedores.',
    icon: Building2,
  },
]

// ─── Step indicator ──────────────────────────────────────────────────────────

function StepIndicator({ step }: { step: 1 | 2 | 3 | 4 }) {
  const labels = ['Entidad', 'Campos', 'Filtros', 'Generar']
  return (
    <div className="flex items-center gap-2 mb-6">
      {labels.map((l, i) => {
        const n = (i + 1) as 1 | 2 | 3 | 4
        const active = n === step
        const done = n < step
        return (
          <div key={n} className="flex items-center gap-2">
            <div className={`
              flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold transition-colors
              ${done
                ? 'bg-primary text-on-primary'
                : active
                  ? 'bg-primary-50 text-primary border-2 border-primary'
                  : 'bg-surface-muted text-content-subtle'}
            `}>
              {done ? <Check size={13} /> : n}
            </div>
            <span className={`text-sm ${active ? 'font-semibold text-content' : done ? 'text-content-muted' : 'text-content-subtle'}`}>
              {l}
            </span>
            {n < 4 && <div className="w-8 h-px bg-divider mx-1" />}
          </div>
        )
      })}
    </div>
  )
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CustomReportPage() {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1)
  const [entity, setEntity] = useState<ReportEntity | null>(null)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [filters, setFilters] = useState<CustomReportFilters>({
    date_from: firstOfMonthIso(),
    date_to: todayIso(),
  })
  const [format, setFormat] = useState<ReportFormat>('xlsx')
  const [asyncMessage, setAsyncMessage] = useState<string | null>(null)

  // Validación de rango de fechas (máx 1 año, desde ≤ hasta)
  const dateRangeError = useMemo(() => {
    const f = filters.date_from
    const t = filters.date_to
    if (!f || !t) return null
    const from = new Date(f)
    const to = new Date(t)
    if (isNaN(from.getTime()) || isNaN(to.getTime())) return null
    if (from > to) return 'La fecha "desde" no puede ser posterior a "hasta".'
    const diffDays = (to.getTime() - from.getTime()) / (86400 * 1000)
    if (diffDays > 366) return 'El rango no puede exceder 1 año. Acota el periodo.'
    return null
  }, [filters.date_from, filters.date_to])

  const schemaQuery = useCustomReportSchema(entity)
  const generateMutation = useGenerateCustomReport()
  const schema = schemaQuery.data
  const sellersQuery = useSellers()

  // Flatten + default selection cuando carga schema
  const allFields: CustomFieldSpec[] = useMemo(() => {
    if (!schema) return []
    return schema.groups.flatMap(g => g.fields)
  }, [schema])

  // ─── Ordenamiento (campo + dirección) ──────────────────────────────────────
  const defaultOrderField = schema?.order_options?.[0]?.key ?? 'issue_date'
  const orderField = (filters.ordering ?? '').replace(/^-/, '') || defaultOrderField
  const orderDir: 'asc' | 'desc' = (filters.ordering ?? '-').startsWith('-') ? 'desc' : 'asc'
  const setOrdering = (field: string, dir: 'asc' | 'desc') => {
    setFilters(f => ({ ...f, ordering: `${dir === 'desc' ? '-' : ''}${field}` }))
  }

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleEntity = (key: ReportEntity) => {
    setEntity(key)
    setSelectedKeys(new Set())
    setFilters({ date_from: firstOfMonthIso(), date_to: todayIso() })
    setAsyncMessage(null)
    setStep(2)
  }

  // Cuando llegan los fields, preselect los default_selected
  const ensureDefaults = () => {
    if (selectedKeys.size === 0 && allFields.length) {
      const next = new Set(allFields.filter(f => f.default_selected).map(f => f.key))
      setSelectedKeys(next)
    }
  }
  if (allFields.length && selectedKeys.size === 0) {
    ensureDefaults()
  }

  const toggleField = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const selectAllInGroup = (group: string, select: boolean) => {
    const groupKeys = allFields.filter(f => f.group === group).map(f => f.key)
    setSelectedKeys(prev => {
      const next = new Set(prev)
      for (const k of groupKeys) {
        if (select) next.add(k)
        else next.delete(k)
      }
      return next
    })
  }

  const applyPreset = (preset: 'minimal' | 'default' | 'all') => {
    if (preset === 'all') setSelectedKeys(new Set(allFields.map(f => f.key)))
    else if (preset === 'default') setSelectedKeys(new Set(allFields.filter(f => f.default_selected).map(f => f.key)))
    else setSelectedKeys(new Set(allFields.filter(f => f.default_selected).slice(0, 4).map(f => f.key)))
  }

  const handleGenerate = async () => {
    if (!entity) return
    setAsyncMessage(null)
    try {
      const result = await generateMutation.mutateAsync({
        entity,
        fields: Array.from(selectedKeys),
        filters,
        format,
      })
      if (result.kind === 'sync') {
        triggerBlobDownload(result.blob, result.filename)
      } else {
        setAsyncMessage(result.message)
      }
    } catch {
      // error state viene de mutation
    }
  }

  const canProceedFromStep2 = selectedKeys.size > 0

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-content flex items-center gap-2">
          <Sparkles size={20} className="text-primary" /> Reportes Personalizados
        </h1>
        <p className="text-sm text-content-muted mt-0.5">
          Elige la entidad, los campos que quieres exportar y los filtros. Genera un XLSX o CSV descargable.
        </p>
      </div>

      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-6 shadow-card">
        <StepIndicator step={step} />

        {/* ─── Step 1: Entity ───────────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-3">
            <h2 className="text-base font-semibold text-content">¿Sobre qué quieres el reporte?</h2>
            <p className="text-sm text-content-muted">Elige la entidad. En el siguiente paso podrás elegir los campos.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              {ENTITIES.map(e => {
                const Icon = e.icon
                return (
                  <button
                    key={e.key}
                    onClick={() => handleEntity(e.key)}
                    className="group text-left p-5 rounded-xl border border-divider hover:border-primary hover:bg-primary-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center group-hover:bg-primary/10">
                        <Icon size={20} className="text-primary" />
                      </div>
                      <p className="font-semibold text-content">{e.label}</p>
                    </div>
                    <p className="text-xs text-content-muted">{e.description}</p>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* ─── Step 2: Fields ─────────────────────────────────── */}
        {step === 2 && entity && (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h2 className="text-base font-semibold text-content">Campos a exportar</h2>
                <p className="text-sm text-content-muted">
                  {selectedKeys.size} de {allFields.length} campos seleccionados.
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => applyPreset('minimal')}>Mínimo</Button>
                <Button variant="ghost" size="sm" onClick={() => applyPreset('default')}>Default</Button>
                <Button variant="ghost" size="sm" onClick={() => applyPreset('all')}>Todos</Button>
              </div>
            </div>

            {schemaQuery.isLoading && (
              <div className="flex items-center gap-2 text-content-subtle text-sm py-8 justify-center">
                <Loader2 size={16} className="animate-spin" /> Cargando campos…
              </div>
            )}

            {schema && (
              <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-2">
                {schema.groups.map(g => {
                  const groupKeys = g.fields.map(f => f.key)
                  const selectedInGroup = groupKeys.filter(k => selectedKeys.has(k)).length
                  const allSelected = selectedInGroup === groupKeys.length
                  return (
                    <div key={g.group} className="border border-divider rounded-lg overflow-hidden">
                      <div className="px-4 py-2 bg-surface-muted border-b border-divider flex items-center justify-between">
                        <p className="text-xs font-semibold uppercase tracking-wider text-content-muted">
                          {g.group}
                          <span className="ml-2 text-content-subtle normal-case font-normal">
                            ({selectedInGroup}/{groupKeys.length})
                          </span>
                        </p>
                        <button
                          onClick={() => selectAllInGroup(g.group, !allSelected)}
                          className="text-xs text-primary hover:underline"
                        >
                          {allSelected ? 'Deseleccionar grupo' : 'Seleccionar grupo'}
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 divide-y sm:divide-y-0 divide-divider">
                        {g.fields.map(f => (
                          <label
                            key={f.key}
                            className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-soft cursor-pointer border-b border-divider sm:border-b-0"
                          >
                            <input
                              type="checkbox"
                              checked={selectedKeys.has(f.key)}
                              onChange={() => toggleField(f.key)}
                              className="w-4 h-4 text-primary rounded"
                            />
                            <span className="text-content flex-1">{f.label}</span>
                            <span className="text-[10px] text-content-subtle uppercase tracking-wider">
                              {TYPE_LABELS[f.type] ?? f.type}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* ─── Step 3: Filters ───────────────────────────────── */}
        {step === 3 && entity && schema && (
          <div className="space-y-4">
            <h2 className="text-base font-semibold text-content">Rango de fechas</h2>
            <p className="text-sm text-content-muted">
              Por defecto, desde el primer día del mes hasta hoy. El rango máximo permitido es <strong>1 año</strong>.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="customreportpage-desde" className={labelCls}>Desde</label>
                <DatePicker id="customreportpage-desde"
                  value={filters.date_from ?? ''}
                  onChange={v => setFilters(f => ({ ...f, date_from: v || undefined }))}
                />
              </div>
              <div>
                <label htmlFor="customreportpage-hasta" className={labelCls}>Hasta</label>
                <DatePicker id="customreportpage-hasta"
                  value={filters.date_to ?? ''}
                  onChange={v => setFilters(f => ({ ...f, date_to: v || undefined }))}
                />
              </div>
            </div>

            {dateRangeError && (
              <div className="flex items-start gap-2 p-3 bg-warning-surface border border-warning-border rounded-lg">
                <AlertTriangle size={16} className="text-warning-content-soft mt-0.5 shrink-0" />
                <p className="text-sm text-warning-content-strong">{dateRangeError}</p>
              </div>
            )}

            {schema.order_options?.length > 0 && (
              <div className="pt-2">
                <h2 className="text-base font-semibold text-content mb-1">Ordenar resultados</h2>
                <p className="text-sm text-content-muted mb-3">
                  Define el orden de las filas. Para ordenar por orden de creación, usa <strong>Fecha de creación</strong>
                  {' '}(el sistema no tiene un número correlativo visible).
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <SelectField
                    dense
                    label="Ordenar por"
                    value={orderField}
                    onChange={e => setOrdering(e.target.value, orderDir)}
                  >
                    {schema.order_options.map(o => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                    ))}
                  </SelectField>
                  <div>
                    <label className={labelCls}>Dirección</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setOrdering(orderField, 'desc')}
                        className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors ${
                          orderDir === 'desc' ? 'border-primary bg-primary-50 text-primary' : 'border-divider text-content-muted hover:bg-soft'
                        }`}
                      >
                        Descendente (más reciente primero)
                      </button>
                      <button
                        type="button"
                        onClick={() => setOrdering(orderField, 'asc')}
                        className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors ${
                          orderDir === 'asc' ? 'border-primary bg-primary-50 text-primary' : 'border-divider text-content-muted hover:bg-soft'
                        }`}
                      >
                        Ascendente (más antiguo primero)
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {schema.filters?.includes('seller') && (
              <div className="pt-2">
                <h2 className="text-base font-semibold text-content mb-1">Ejecutivo comercial</h2>
                <p className="text-sm text-content-muted mb-3">
                  Filtra las ventas por el vendedor del <strong>pedido de origen</strong>.
                  Las facturas emitidas sin un pedido asociado no tienen ejecutivo y no
                  aparecerán al filtrar. Agrega la columna <strong>Vendedor</strong> (grupo
                  Comercial) en el paso anterior para verlo en el reporte.
                </p>
                <select aria-label="Todos los vendedores"
                  value={filters.seller_id ?? ''}
                  onChange={e => setFilters(f => ({ ...f, seller_id: e.target.value || undefined }))}
                  className="w-full sm:max-w-sm px-3 py-2 rounded-lg border border-divider bg-surface text-content text-sm focus:ring-2 focus:ring-focus-ring"
                >
                  <option value="">Todos los vendedores</option>
                  {(sellersQuery.data?.sellers ?? []).map(s => (
                    <option key={s.id} value={s.id}>{s.full_name || s.email}</option>
                  ))}
                </select>
              </div>
            )}

            <p className="text-xs text-content-subtle">
              Próximamente: filtros por estado, tipo de documento, cliente/proveedor y más.
            </p>
          </div>
        )}

        {/* ─── Step 4: Generate ───────────────────────────────── */}
        {step === 4 && entity && schema && (
          <div className="space-y-4">
            <h2 className="text-base font-semibold text-content">Generar reporte</h2>
            <p className="text-sm text-content-muted">
              Entidad: <strong>{schema.label}</strong> · Campos: <strong>{selectedKeys.size}</strong> ·
              Filtros: <strong>{Object.values(filters).filter(v => v !== undefined && v !== '').length}</strong>
            </p>

            <div>
              <label className={labelCls}>Formato</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setFormat('xlsx')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors ${
                    format === 'xlsx' ? 'border-primary bg-primary-50 text-primary' : 'border-divider text-content-muted hover:bg-soft'
                  }`}
                >
                  <FileSpreadsheet size={16} /> Excel (.xlsx)
                </button>
                <button
                  onClick={() => setFormat('csv')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-colors ${
                    format === 'csv' ? 'border-primary bg-primary-50 text-primary' : 'border-divider text-content-muted hover:bg-soft'
                  }`}
                >
                  <FileText size={16} /> CSV (.csv)
                </button>
              </div>
              <p className="text-xs text-content-subtle mt-2">
                XLSX incluye formato de números, fechas y auto-ajuste de columnas. CSV es útil para importar a otros sistemas.
              </p>
            </div>

            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <Mail size={16} className="text-blue-600 dark:text-blue-300 mt-0.5 shrink-0" />
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Si el reporte supera las <strong>{schema.sync_threshold.toLocaleString('es-CL')}</strong> filas,
                lo generaremos en background y te enviaremos un enlace por email.
              </p>
            </div>

            {asyncMessage && (
              <div className="flex items-start gap-2 p-3 bg-success-surface border border-success-border rounded-lg">
                <CheckCircle2 size={16} className="text-success-content-soft mt-0.5 shrink-0" />
                <p className="text-sm text-success-content-strong">{asyncMessage}</p>
              </div>
            )}

            {generateMutation.isError && (
              <div className="flex items-start gap-2 p-3 bg-danger-surface border border-danger-border rounded-lg">
                <AlertTriangle size={16} className="text-danger-content-soft mt-0.5 shrink-0" />
                <p className="text-sm text-danger-content-strong">
                  {(generateMutation.error as Error)?.message || 'Error al generar el reporte.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* ─── Footer nav ───────────────────────────────────── */}
        <div className="flex justify-between pt-6 mt-6 border-t border-divider">
          <div>
            {step > 1 && (
              <button
                onClick={() => setStep((s) => (Math.max(1, s - 1) as 1 | 2 | 3 | 4))}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors"
              >
                <ArrowLeft size={14} /> Atrás
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {step < 4 && step > 1 && (
              <Button
                onClick={() => setStep((s) => (Math.min(4, s + 1) as 1 | 2 | 3 | 4))}
                disabled={(step === 2 && !canProceedFromStep2) || (step === 3 && !!dateRangeError)}
                rightIcon={<ArrowRight size={14} />}
              >
                Siguiente
              </Button>
            )}
            {step === 4 && (
              <Button
                onClick={handleGenerate}
                isPending={generateMutation.isPending}
                pendingLabel="Generando..."
                disabled={!!dateRangeError}
                leftIcon={<Download size={14} />}
              >
                Generar reporte
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
